import { useEffect, useState } from "react";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, BarChart, Bar, CartesianGrid, Legend } from "recharts";
import { Loader2, Scale, Flame, TreePine, Eye, EyeOff, Sparkles } from "lucide-react";
import GrowingTree from "@/components/GrowingTree";

const GOAL_FR = { perte_poids: "Perte de poids", prise_muscle: "Prise de muscle", remise_forme: "Remise en forme" };

export default function ProgressView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weight, setWeight] = useState("");
  const [busy, setBusy] = useState(false);
  const [showWeight, setShowWeight] = useState(true);

  const load = async () => {
    try {
      const { data } = await apiClient.get("/progress");
      setData(data);
    } catch (e) {} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveWeight = async () => {
    if (!weight) { toast.error("Indique ton poids."); return; }
    setBusy(true);
    try {
      await apiClient.post("/progress", { weight_kg: parseFloat(weight), sessions_completed: 0 });
      toast.success("Pesée enregistrée ✅");
      setWeight("");
      load();
    } catch (e) { toast.error("Erreur d'enregistrement."); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-lime-400" /></div>;

  const weekly = (data?.weekly || []).map((w) => ({ name: `S${w.week}`, Réalisées: w.done, Prévues: w.planned }));
  const weightData = (data?.weight_entries || []).map((e, i) => ({
    name: new Date(e.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" }),
    poids: e.weight_kg,
  }));
  const garden = data?.garden || [];
  const streak = data?.streak_weeks || 0;

  return (
    <div className="space-y-4">
      {/* Central current-cycle tree */}
      <div data-testid="progress-tree-card" className="rounded-2xl border border-white/10 bg-[#141C17] p-6 text-center">
        <div className="flex items-center justify-center gap-2 mb-1">
          <TreePine className="h-4 w-4 text-lime-400" />
          <h3 className="font-display font-bold">Ton arbre du cycle {data?.current_cycle || 1}</h3>
        </div>
        <p className="text-xs text-white/40 mb-4">Il grandit à chaque séance terminée 🌱</p>
        <GrowingTree score={data?.tree_score || 0} stage={data?.tree_stage || "Graine"} />
        <div className="grid grid-cols-3 gap-3 mt-5">
          <div className="rounded-xl bg-white/[0.03] border border-white/8 p-3">
            <p className="text-2xl font-black text-lime-400" data-testid="current-sessions">{data?.current_sessions || 0}</p>
            <p className="text-xs text-white/40">séances</p>
          </div>
          <div className="rounded-xl bg-white/[0.03] border border-white/8 p-3">
            <p className="text-2xl font-black text-lime-400" data-testid="total-sessions">{data?.total_sessions || 0}</p>
            <p className="text-xs text-white/40">au total</p>
          </div>
          <div className="rounded-xl bg-white/[0.03] border border-white/8 p-3">
            <p className="text-2xl font-black text-lime-400" data-testid="streak-weeks">{streak}</p>
            <p className="text-xs text-white/40">sem. de suite</p>
          </div>
        </div>
      </div>

      {/* Regularity */}
      <div data-testid="regularity-card" className="rounded-2xl border border-lime-400/25 bg-lime-400/[0.05] p-4 flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-lime-400/15 flex items-center justify-center shrink-0">
          <Flame className="h-5 w-5 text-lime-400" />
        </div>
        <div>
          <p className="font-semibold text-sm">
            {streak > 0 ? `${streak} semaine${streak > 1 ? "s" : ""} d'affilée avec au moins une séance` : "Lance ta première séance cette semaine"}
          </p>
          <p className="text-xs text-white/50">La régularité fait grandir ton arbre, pas le poids.</p>
        </div>
      </div>

      {/* Sessions done vs planned per week */}
      <div data-testid="weekly-chart-card" className="rounded-2xl border border-white/10 bg-[#141C17] p-5">
        <h3 className="font-display font-bold mb-4 text-sm">Séances réalisées vs prévues (cycle en cours)</h3>
        <ResponsiveContainer width="100%" height={190}>
          <BarChart data={weekly} barGap={4}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="name" stroke="#6b7280" fontSize={11} />
            <YAxis stroke="#6b7280" fontSize={11} allowDecimals={false} />
            <Tooltip contentStyle={{ background: "#0A0F0D", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="Prévues" fill="#3f4d43" radius={[6, 6, 0, 0]} />
            <Bar dataKey="Réalisées" fill="#C8FF00" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Weekly optional weigh-in */}
      <div className="rounded-2xl border border-white/10 bg-[#141C17] p-5">
        <div className="flex items-center gap-2 mb-1">
          <Scale className="h-5 w-5 text-lime-400" />
          <h3 className="font-display font-bold">Pesée hebdomadaire</h3>
        </div>
        <p className="text-xs text-white/40 mb-4">Optionnelle, jamais obligatoire.</p>
        <div className="flex gap-2">
          <input data-testid="weight-input" type="number" placeholder="Poids actuel (kg)" value={weight}
            onChange={(e) => setWeight(e.target.value)}
            className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-lime-400/50" />
          <button data-testid="save-weight-button" onClick={saveWeight} disabled={busy}
            className="inline-flex items-center justify-center gap-2 bg-lime-400 text-black font-extrabold px-5 rounded-xl hover:bg-[#b0e600] active:scale-95 transition-all disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "OK"}
          </button>
        </div>
      </div>

      {/* Weight curve with hide toggle */}
      {weightData.length > 0 && (
        <div data-testid="weight-chart-card" className="rounded-2xl border border-white/10 bg-[#141C17] p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-sm">Évolution du poids</h3>
            <button data-testid="toggle-weight-chart" onClick={() => setShowWeight((v) => !v)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/60 border border-white/15 rounded-lg px-3 py-1.5 hover:bg-white/5">
              {showWeight ? <><EyeOff className="h-3.5 w-3.5" /> Masquer</> : <><Eye className="h-3.5 w-3.5" /> Afficher</>}
            </button>
          </div>
          {showWeight && (
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={weightData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="name" stroke="#6b7280" fontSize={11} />
                <YAxis stroke="#6b7280" fontSize={11} domain={["auto", "auto"]} />
                <Tooltip contentStyle={{ background: "#0A0F0D", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} />
                <Line type="monotone" dataKey="poids" stroke="#C8FF00" strokeWidth={2.5} dot={{ fill: "#C8FF00", r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      )}

      {/* Garden — all cycles */}
      {garden.length > 0 && (
        <div data-testid="garden-card" className="rounded-2xl border border-white/10 bg-[#141C17] p-5">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="h-5 w-5 text-lime-400" />
            <h3 className="font-display font-bold">Mon jardin</h3>
          </div>
          <p className="text-xs text-white/40 mb-4">Chaque cycle plante un nouvel arbre. Tout le chemin parcouru reste visible. 🌳</p>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {garden.map((g) => (
              <div key={g.cycle} className="shrink-0 w-28 rounded-xl bg-white/[0.03] border border-white/8 p-2 text-center">
                <GrowingTree score={g.tree_score} stage={g.tree_stage} compact
                  label={`Cycle ${g.cycle}`} />
                <p className="text-[10px] text-lime-300 font-semibold mt-1">{g.tree_stage}</p>
                <p className="text-[10px] text-white/40">{g.sessions} séance{g.sessions > 1 ? "s" : ""}</p>
                {g.goal && <p className="text-[9px] text-white/30 mt-0.5">{GOAL_FR[g.goal] || g.goal}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
